import { useEffect, useRef, useState } from "react";
import { ArrowRight, CalendarClock, FileText, Phone, ShieldCheck } from "lucide-react";

import { Ideal4FinancePartnerBanner } from "@/components/financing/ideal4finance-partner-banner";
import { FINANCE_CALCULATOR_PATH } from "@/content/finance";
import { FINANCING_I4F_HOW_IT_WORKS_PATH } from "@/content/financing-i4f-how-it-works";
import {
  FINANCE_SPREAD_COST_COPY,
  FINANCE_TERMS_BEFORE_COMMIT_COPY,
} from "@/content/landing/financing";

const PHONE_TEL = "tel:08000092992";

function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.1 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, shown };
}

const benefits = [
  {
    Icon: CalendarClock,
    title: "Monthly Payment Options",
    body: "Spread the cost over an agreed term with a fixed monthly repayment.",
  },
  {
    Icon: FileText,
    title: "Full Terms Up Front",
    body: "Every agreement shows the complete cost and term before you commit.",
  },
  {
    Icon: ShieldCheck,
    title: "No Obligation",
    body: "Finance is a payment option, not a requirement. You can pay for your installation without it.",
  },
];

export function HomeFinanceDetails() {
  const panel = useReveal<HTMLDivElement>();

  return (
    <div
      ref={panel.ref}
      className={`transition-all duration-500 ease-out motion-reduce:transition-none ${
        panel.shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      }`}
    >
      <div className="rounded-2xl border border-border bg-card shadow-lift overflow-hidden">
        <div className="p-6 md:p-10">
          <Ideal4FinancePartnerBanner className="mb-6 md:mb-8 max-w-3xl" />

          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">Finance</p>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground leading-tight">
              Your Payment Options
            </h2>
            <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">
              Finance is provided through Ideal4Finance. Monthly payment options may be available
              for insulation, heating, solar and home repairs, subject to status.
            </p>
          </div>

          <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {benefits.map(({ Icon, title, body }) => (
              <div
                key={title}
                className="rounded-2xl border border-border bg-background p-5 shadow-sm"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent/10 text-accent">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <h3 className="mt-3 text-sm md:text-base font-semibold text-foreground leading-snug">
                  {title}
                </h3>
                <p className="mt-1.5 text-[13px] md:text-sm text-muted-foreground leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-border/60 bg-accent-soft/[0.35] p-6 md:p-10">
          <div className="max-w-xl">
            <h3 className="text-xl md:text-2xl font-bold text-foreground leading-tight">
              Spread The Cost
            </h3>
            <p className="mt-2 text-sm md:text-base text-foreground/80 leading-relaxed">
              {FINANCE_SPREAD_COST_COPY}
            </p>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {FINANCE_TERMS_BEFORE_COMMIT_COPY}
            </p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-4">
            <a
              href={FINANCE_CALCULATOR_PATH}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold shadow-cta hover:bg-accent/90 transition-colors"
            >
              Work out the monthly payments
              <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
            <a
              href={FINANCING_I4F_HOW_IT_WORKS_PATH}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-background px-6 py-3 font-semibold text-foreground shadow-sm hover:bg-secondary/50 transition-colors"
            >
              See how applying works
              <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
          </div>
        </div>

        <div className="border-t border-border/60 bg-muted/50 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <p className="text-sm md:text-base text-foreground/85 leading-relaxed max-w-xl">
              Prefer to talk it through first? Book a call or speak to our team. No obligation.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 shrink-0">
              <a
                href="#home-hero-form"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 sm:py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors"
              >
                Book A Call To Discuss Finance
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={PHONE_TEL}
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-accent hover:text-accent/80 transition-colors"
              >
                <Phone className="h-4 w-4" aria-hidden />
                or call 0800 009 2992
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
